import { useEffect, useMemo, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { ChatMessage, StreamEvent } from '@hypevault/shared-types';
import { WidgetView } from './widgets/WidgetView';
import { GemLeaderboardWidget } from './GemLeaderboardWidget';
import { HypePetsLeaderboardWidget } from './HypePetsLeaderboardWidget';
import { ChaosScrambleWidget } from './widgets/ChaosScrambleWidget';
import { EmojiDecoderWidget } from './widgets/EmojiDecoderWidget';
import { HypeMillionWidget } from './widgets/HypeMillionWidget';
import { HypeDealWidget } from './widgets/HypeDealWidget';
import { DrawGuessWidget } from './widgets/DrawGuessWidget';
import { MagicPoolBallWidget, type MagicPoolBallEvent } from './widgets/MagicPoolBall';
import { SlotsWidget, type SlotsEvent } from './widgets/Slots';
import { UnifiedGameWidget, type ActiveGameType } from './widgets/UnifiedGameWidget';
import { JumpscareWidget, type JumpscarePayload } from './widgets/JumpscareWidget';
import type { RoundSnapshot } from '@hypevault/chaos-scramble';
import type { RoundSnapshot as EmojiSnapshot } from '@hypevault/emoji-decoder';
import type { GameSnapshot as HypeMillionSnapshot } from '@hypevault/hype-million';
import type { GameSnapshot as HypeDealSnapshot } from '@hypevault/hype-deal';
import type { RoundSnapshot as DrawGuessSnapshot } from '@hypevault/draw-guess';

interface Transform {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  opacity: number;
  zIndex: number;
  locked: boolean;
  hidden: boolean;
  flipX?: boolean;
  flipY?: boolean;
  animationIn?: string;
  animationInDuration?: number;
  animationInDelay?: number;
  animationOut?: string;
  animationOutDuration?: number;
  name?: string;
}

interface WidgetInstance {
  id: string;
  widgetType: string;
  transform: Transform;
  fields: Record<string, unknown>;
}

interface Scene {
  id: string;
  name: string;
  widgets: WidgetInstance[];
}

interface OverlayResponse {
  overlay: {
    id: string;
    width: number;
    height: number;
    activeSceneId: string | null;
    version: number;
    backgroundColor?: string | null;
  };
  scenes: Scene[];
  streamerId: string;
}

const WS_URL = import.meta.env.VITE_WS_URL ?? 'http://localhost:3002';

// Inline @keyframes so streamer-side widget animations don't need a separate
// CSS file. All keyframes map to the editor's animation dropdown values.
const ANIMATION_CSS = `
@keyframes hv-fade-in { from { opacity: 0 } to { opacity: 1 } }
@keyframes hv-slide-in-left { from { transform: translateX(-100%); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
@keyframes hv-slide-in-right { from { transform: translateX(100%); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
@keyframes hv-slide-in-up { from { transform: translateY(100%); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
@keyframes hv-slide-in-down { from { transform: translateY(-100%); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
@keyframes hv-zoom-in { from { transform: scale(0); opacity: 0 } to { transform: scale(1); opacity: 1 } }
@keyframes hv-bounce-in { 0% { transform: scale(0.3); opacity: 0 } 50% { transform: scale(1.05); opacity: 1 } 70% { transform: scale(0.9) } 100% { transform: scale(1) } }
@keyframes hv-flip-in { from { transform: perspective(400px) rotateY(90deg); opacity: 0 } to { transform: perspective(400px) rotateY(0); opacity: 1 } }
.hv-scene-fade { transition: opacity 300ms ease }
`;

export function OverlayApp() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const [data, setData] = useState<OverlayResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recentEvent, setRecentEvent] = useState<StreamEvent | null>(null);
  const [chatMessage, setChatMessage] = useState<ChatMessage | null>(null);
  const [gameStateEvent, setGameStateEvent] = useState<{
    gameType: string;
    state: unknown;
    config: unknown;
    isActive: boolean;
  } | null>(null);
  const [chaosSnapshot, setChaosSnapshot] = useState<RoundSnapshot | null>(null);
  const [emojiSnapshot, setEmojiSnapshot] = useState<EmojiSnapshot | null>(null);
  const [hypeMillionSnapshot, setHypeMillionSnapshot] = useState<HypeMillionSnapshot | null>(null);
  const [hypeDealSnapshot, setHypeDealSnapshot] = useState<HypeDealSnapshot | null>(null);
  const [drawGuessSnapshot, setDrawGuessSnapshot] = useState<DrawGuessSnapshot | null>(null);
  const [magicPoolBallEvent, setMagicPoolBallEvent] = useState<MagicPoolBallEvent | null>(null);
  const [slotsEvent, setSlotsEvent] = useState<SlotsEvent | null>(null);
  const [jumpscareEvent, setJumpscareEvent] = useState<JumpscarePayload | null>(null);
  const [activeGame, setActiveGame] = useState<ActiveGameType | null | undefined>(undefined);
  // Live-overridable active scene — the bootstrap response gives us the
  // initial one, but the scene-switch event from the dashboard updates this
  // without a refresh. We also crossfade on switches via opacity.
  const [activeSceneId, setActiveSceneId] = useState<string | null>(null);
  const [crossfading, setCrossfading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Missing token');
      return;
    }
    fetch(`/api/overlays/public?token=${encodeURIComponent(token)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((json: OverlayResponse) => {
        setData(json);
        setActiveSceneId(json.overlay.activeSceneId);
      })
      .catch((err) => setError(String(err)));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const socket: Socket = io(WS_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socket.on('event', (event: StreamEvent | { type: string; sceneId?: string }) => {
      // The same event channel carries scene-switch control messages now —
      // they aren't real StreamEvents (no id/streamerId), so detect by shape.
      if ((event as { type?: string }).type === 'scene-switch') {
        const sceneId = (event as { sceneId?: string }).sceneId ?? null;
        if (!sceneId) return;
        setCrossfading(true);
        setTimeout(() => {
          setActiveSceneId(sceneId);
          setCrossfading(false);
        }, 300);
        return;
      }
      const se = event as StreamEvent;
      setRecentEvent(se);
    });
    socket.on('chat-message', (msg: ChatMessage) => {
      setChatMessage(msg);
    });
    socket.on(
      'game-state',
      (e: { gameType: string; state: unknown; config: unknown; isActive: boolean }) =>
        setGameStateEvent(e),
    );
    socket.on('chaos-state', (e: RoundSnapshot) => setChaosSnapshot(e));
    socket.on('emoji-state', (e: EmojiSnapshot | { ended: true }) => {
      // Runner publishes `{ ended: true }` on Stop. Reset to null so the
      // widget falls back to its IdleStage instead of trying to render
      // Stage with undefined fields (which crashes on .toLocaleString()).
      if (e && 'ended' in e) setEmojiSnapshot(null);
      else setEmojiSnapshot(e);
    });
    socket.on('hype-million-state', (e: HypeMillionSnapshot) => setHypeMillionSnapshot(e));
    socket.on('hype-deal-state', (e: HypeDealSnapshot | null) => setHypeDealSnapshot(e));
    socket.on('draw-guess-state', (e: DrawGuessSnapshot | { ended: true }) => {
      if ('ended' in e) {
        setDrawGuessSnapshot(null);
      } else {
        setDrawGuessSnapshot(e);
      }
    });
    socket.on('magic-pool-ball-trigger', (e: MagicPoolBallEvent) => setMagicPoolBallEvent(e));
    socket.on('slots-trigger', (e: SlotsEvent) => setSlotsEvent(e));
    socket.on(
      'jumpscare',
      (frame: { type?: string; payload?: JumpscarePayload } | JumpscarePayload) => {
        // Envelope tolerance: eventbus uses { type, payload }, some callers
        // emit the payload directly. Same sniff as hype-vison/websocket.ts.
        const p =
          frame && 'payload' in frame && frame.payload
            ? frame.payload
            : (frame as JumpscarePayload);
        if (!p?.mediaUrl) return;
        setJumpscareEvent(p);
      },
    );
    socket.on('active-game-changed', (e: { activeGame: ActiveGameType | null }) =>
      setActiveGame(e?.activeGame ?? null),
    );
    return () => {
      socket.disconnect();
    };
  }, [token]);

  if (error) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 12,
          left: 12,
          color: '#f87171',
          fontFamily: 'monospace',
          fontSize: 12,
        }}
      >
        Overlay error: {error}
      </div>
    );
  }

  if (!data) return null;

  const activeScene = data.scenes.find((s) => s.id === activeSceneId) ?? data.scenes[0];

  return (
    <>
      <style>{ANIMATION_CSS}</style>
      <div
        className="hv-scene-fade"
        style={{
          position: 'fixed',
          inset: 0,
          width: data.overlay.width,
          height: data.overlay.height,
          backgroundColor: data.overlay.backgroundColor ?? 'transparent',
          opacity: crossfading ? 0 : 1,
          transformOrigin: 'top left',
        }}
      >
        {activeScene?.widgets
          .filter((w) => !w.transform.hidden)
          .sort((a, b) => a.transform.zIndex - b.transform.zIndex)
          .map((w) => (
            <WidgetWrapper
              key={w.id}
              widget={w}
              token={token!}
              streamerId={data.streamerId}
              latestEvent={recentEvent}
              latestChatMessage={chatMessage}
              gameStateEvent={gameStateEvent}
              chaosSnapshot={chaosSnapshot}
              emojiSnapshot={emojiSnapshot}
              hypeMillionSnapshot={hypeMillionSnapshot}
              hypeDealSnapshot={hypeDealSnapshot}
              drawGuessSnapshot={drawGuessSnapshot}
              magicPoolBallEvent={magicPoolBallEvent}
              slotsEvent={slotsEvent}
              jumpscareEvent={jumpscareEvent}
              activeGame={activeGame}
            />
          ))}
      </div>
    </>
  );
}

function WidgetWrapper({
  widget,
  token,
  streamerId,
  latestEvent,
  latestChatMessage,
  gameStateEvent,
  chaosSnapshot,
  emojiSnapshot,
  hypeMillionSnapshot,
  hypeDealSnapshot,
  drawGuessSnapshot,
  magicPoolBallEvent,
  slotsEvent,
  jumpscareEvent,
  activeGame,
}: {
  widget: WidgetInstance;
  token: string;
  streamerId: string;
  latestEvent: StreamEvent | null;
  latestChatMessage: ChatMessage | null;
  gameStateEvent: { gameType: string; state: unknown; config: unknown; isActive: boolean } | null;
  chaosSnapshot: RoundSnapshot | null;
  emojiSnapshot: EmojiSnapshot | null;
  hypeMillionSnapshot: HypeMillionSnapshot | null;
  hypeDealSnapshot: HypeDealSnapshot | null;
  drawGuessSnapshot: DrawGuessSnapshot | null;
  magicPoolBallEvent: MagicPoolBallEvent | null;
  slotsEvent: SlotsEvent | null;
  jumpscareEvent: JumpscarePayload | null;
  activeGame: ActiveGameType | null | undefined;
}) {
  const t = widget.transform;
  // CSS animation string — only set when animateIn is configured. Once the
  // animation runs once on mount we leave the property in place; the keyframe
  // playback only happens at mount-time since `animation` is idempotent.
  const animation = useMemo(() => {
    if (!t.animationIn || t.animationIn === 'none') return undefined;
    return `hv-${t.animationIn} ${t.animationInDuration ?? 0.6}s ${t.animationInDelay ?? 0}s both`;
  }, [t.animationIn, t.animationInDuration, t.animationInDelay]);

  const transform = `rotate(${t.rotation}deg) scaleX(${t.flipX ? -1 : 1}) scaleY(${t.flipY ? -1 : 1})`;

  return (
    <div
      style={{
        position: 'absolute',
        left: t.x,
        top: t.y,
        width: t.w,
        height: t.h,
        opacity: t.opacity,
        transform,
        transformOrigin: 'top left',
        animation,
      }}
    >
      {widget.widgetType === 'gem_leaderboard' ? (
        <GemLeaderboardWidget streamerId={streamerId} fields={widget.fields} />
      ) : widget.widgetType === 'hypepets_leaderboard' ? (
        <HypePetsLeaderboardWidget streamerId={streamerId} fields={widget.fields} />
      ) : widget.widgetType === 'game' ? (
        <UnifiedGameWidget
          streamerId={streamerId}
          token={token}
          streamerTier="free"
          fields={widget.fields}
          liveActiveGame={activeGame}
          gameStateEvent={gameStateEvent}
          chaosSnapshot={chaosSnapshot}
          emojiSnapshot={emojiSnapshot}
          hypeMillionSnapshot={hypeMillionSnapshot}
          hypeDealSnapshot={hypeDealSnapshot}
          drawGuessSnapshot={drawGuessSnapshot}
        />
      ) : widget.widgetType === 'chaos_scramble' ? (
        <ChaosScrambleWidget
          streamerId={streamerId}
          snapshot={chaosSnapshot}
          fields={widget.fields}
        />
      ) : widget.widgetType === 'emoji_decoder' ? (
        <EmojiDecoderWidget
          streamerId={streamerId}
          snapshot={emojiSnapshot}
          fields={widget.fields}
        />
      ) : widget.widgetType === 'hype_million' ? (
        <HypeMillionWidget
          streamerId={streamerId}
          snapshot={hypeMillionSnapshot}
          fields={widget.fields}
        />
      ) : widget.widgetType === 'hype_deal' ? (
        <HypeDealWidget
          streamerId={streamerId}
          snapshot={hypeDealSnapshot}
          fields={widget.fields}
        />
      ) : widget.widgetType === 'draw_guess' ? (
        <DrawGuessWidget
          streamerId={streamerId}
          snapshot={drawGuessSnapshot}
          fields={widget.fields}
        />
      ) : widget.widgetType === 'magic_pool_ball' ? (
        <MagicPoolBallWidget
          streamerId={streamerId}
          event={magicPoolBallEvent}
          fields={widget.fields}
        />
      ) : widget.widgetType === 'slots' ? (
        <SlotsWidget streamerId={streamerId} event={slotsEvent} fields={widget.fields} />
      ) : widget.widgetType === 'jumpscare' ? (
        <JumpscareWidget event={jumpscareEvent} fields={widget.fields} />
      ) : (
        <WidgetView
          widget={widget}
          latestEvent={latestEvent}
          latestChatMessage={latestChatMessage}
          token={token}
          gameStateEvent={gameStateEvent}
        />
      )}
    </div>
  );
}
